import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { getUrlParamsFetch, notFoundIdHandler } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  RecordatorioPago,
  RecordatorioPagoPagingRes,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchRecordatorioPagos = (
  params?: GetRecordatorioPagosParams
) => {
  return useQuery({
    queryKey: ['recordatorioPagos', ...Object.values(params || {})],
    queryFn: async () => {
      return getRecordatorioPagos(params);
    },
  });
};

export const useGetRecordatorioPago = (id: string) => {
  return useQuery({
    queryKey: ['recordatorioPago', id],
    queryFn: async () => {
      return getRecordatorioPago(id);
    },
  });
};

export const useCreateRecordatorioPago = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createRecordatorioPago,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recordatorioPagos'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useUpdateRecordatorioPago = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateRecordatorioPago,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recordatorioPagos'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

///* axios
export type GetRecordatorioPagosParams = {
  page?: number;
  page_size?: number;
  nombre?: string;
  codigo?: string;
};

export type CreateRecordatorioPagoParams = Omit<
  RecordatorioPago,
  'id_recordatorio_pago'
>;
export type UpdateRecordatorioPagoParams = {
  id: string;
  data: CreateRecordatorioPagoParams;
};

export const getRecordatorioPagos = async (
  params?: GetRecordatorioPagosParams
) => {
  const queryParams = getUrlParamsFetch(params || {});

  return get<RecordatorioPagoPagingRes>(
    `${urlBackend}api/v1/recordatorios-pago/?${queryParams}`,
    true
  );
};

export const getRecordatorioPago = async (id: string) => {
  const res = await get<RecordatorioPago>(
    `${urlBackend}api/v1/recordatorios-pago/${id}/`,
    true
  );

  notFoundIdHandler(res);
  return res;
};

export const createRecordatorioPago = async (
  data: CreateRecordatorioPagoParams
) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<RecordatorioPago>(
    `${urlBackend}api/v1/recordatorios-pago/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateRecordatorioPago = async ({
  id,
  data,
}: UpdateRecordatorioPagoParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<RecordatorioPago>(
    `${urlBackend}api/v1/recordatorios-pago/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
